import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { ThreeCircles } from 'react-loader-spinner';
import Select from 'react-select';

import AppModal from './AppModal';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';

import Button from '@mui/material/Button';
import SaveIcon from '@mui/icons-material/Save';

import { jsPDF } from "jspdf";
import 'jspdf-autotable';

import './UsersAlertsManagementReport.scss';

import ExcelJS from 'exceljs';
import UIDateFormater, { DateType } from '../../containers/UIhelper/UIDateFormater';
import { applyPdfRtlSupport, registerHebrewPdfFont } from '../../containers/UIhelper/pdfFonts';
import { fetchUsersAlertsManagmentReport, fetchUsersAlertsManagmentReportRange } from '../../actions/CustomersActions';
import { validateReportRange, reportFileLabel } from './usersReportRange';

const xlsColumns = [
  { title: "Leak ID", key: 'id', width: { wch: 20 } },
  { title: "Project", key: 'project', width: { wch: 25 } },
  { title: "User Name", key: 'user', width: { wch: 20 } },
  { title: "Date", key: 'date', width: { wch: 12 } },
  { title: "Time", key: 'time', width: { wch: 12 } },
  { title: "Source", key: 'source', width: { wch: 20 } },
  { title: "Type", key: 'type', width: { wch: 25 } },
  { title: "Status", key: 'status', width: { wch: 25 } },
  { title: "Comment", key: 'comment', width: { wch: 75 } },
  { title: "Address", key: 'address', width: { wch: 35 } },
];

// Shared by the on-screen table and the Excel/PDF exports so they stay in sync.
function getCellValue(row, key, timeZone) {
  switch (key) {
    case 'date':
      return UIDateFormater(row.date, DateType.DATE, timeZone);
    case 'time':
      return UIDateFormater(row.date, DateType.TIME, timeZone);
    case 'address':
      return row.address || row.StreetAddress || '';
    default:
      return row[key] == null ? '' : row[key];
  }
}

const getSortValue = (row, key) => {
  if (key === 'date' || key === 'time') {
    return row.date;
  }
  if (key === 'id') {
    return Number(row.id);
  }
  if (key === 'address') {
    return row.address || row.StreetAddress || '';
  }
  return row[key] == null ? '' : row[key];
};

const sortRows = (data, sortConfig) => {
  if (!sortConfig.key) {
    return data;
  }
  const factor = sortConfig.direction === 'asc' ? 1 : -1;
  return [...data].sort((a, b) => {
    const av = getSortValue(a, sortConfig.key);
    const bv = getSortValue(b, sortConfig.key);
    if (av < bv) return -1 * factor;
    if (av > bv) return 1 * factor;
    return 0;
  });
};

const exportButtonStyle = { width: 'max-content', fontSize: 14, fontWeight: 600 };


const ReportFilter = ({ context, isGenereated, onGeneratedReport, mode, setMode, selectedMonth, setSelectedMonth, customFrom, setCustomFrom, customTo, setCustomTo, includeAqs, setIncludeAqs, usersList, setUsersFilter }) => {
  const selectMonth = useCallback((e) => {
    setSelectedMonth(e.target.value);
  }, []);

  const onClickGeneratedReport = useCallback(() => {
    onGeneratedReport({ mode, month: selectedMonth, from: customFrom, to: customTo });
  }, [mode, selectedMonth, customFrom, customTo, onGeneratedReport]);

  const today = new Date().toISOString().split('T')[0];
  const range = validateReportRange(customFrom, customTo);
  // Only surface the error once both dates are chosen; a half-filled form
  // isn't a mistake yet, just disable Generate.
  const showRangeError = mode === 'custom' && customFrom && customTo && !range.valid;
  const generateDisabled = isGenereated || (mode === 'custom' && !range.valid);

  return (
    <Fragment>
      {['monthly', 'custom'].map((opt) => (
        <div key={opt} className='checkbox-iq100b-mobile'>
          <input type='checkbox' onChange={() => setMode(opt)} checked={mode === opt} value={opt} />
          <label onClick={() => setMode(opt)} style={mode !== opt ? { backgroundColor: '#fefefe', color: 'gray' } : undefined}>
            {context.t(opt)}
          </label>
        </div>
      ))}
      {mode === 'monthly' ? (
        <div className='checkbox-iq100b-mobile-type-view-monthly'>
          <input id='date' type="month" min="2016-01" onChange={selectMonth} value={selectedMonth} />
        </div>
      ) : (
        <div className='checkbox-iq100b-mobile-type-view-daily'>
          <div>
            <label>{context.t('from')}&nbsp;</label>
            <input type='date' min='2016-01-01' max={today} onChange={(e) => setCustomFrom(e.target.value)} value={customFrom} />
          </div>
          <div>
            <label>{context.t('to')}:&nbsp;</label>
            <input type='date' min='2016-01-01' max={today} onChange={(e) => setCustomTo(e.target.value)} value={customTo} />
          </div>
          {showRangeError && (
            <div style={{ color: '#c62828', fontSize: 12, marginTop: 4 }}>
              {context.t(range.error)}
            </div>
          )}
        </div>
      )}
      <div className='checkbox-iq100b-mobile-type-view-monthly'>
        <label>
          <input id='include-aqs' type='checkbox' onChange={(e) => setIncludeAqs(e.target.checked)} value={includeAqs} />
          <span style={{ marginLeft: '5px' }}>include aqs</span>
        </label>
      </div>

      <div className='checkbox-iq100b-mobile-button-view'>
        <Button disabled={generateDisabled} style={exportButtonStyle} variant='contained' color='primary' onClick={onClickGeneratedReport}>
          {context.t('generate_report')}
        </Button>
      </div>
      {usersList && usersList.length > 0 && <div className='checkbox-iq100b-mobile-button-view'>
        <Select
          isMulti
          closeMenuOnSelect={false}
          options={usersList.map(u => ({ label: u, value: u }))}
          onChange={setUsersFilter}
        />
      </div>}
    </Fragment>
  );
};

ReportFilter.propTypes = {
  context: PropTypes.object,
  isGenereated: PropTypes.bool,
  onGeneratedReport: PropTypes.func,
};

function ManagementReportTable({ data, timeZone, sortConfig, onSort }) {
  return (
    <TableContainer className='uamr-table-container'>
      <Table stickyHeader size='small'>
        <TableHead>
          <TableRow>
            {xlsColumns.map((col) => (
              <TableCell
                key={`header_${col.key}`}
                sortDirection={sortConfig.key === col.key ? sortConfig.direction : false}
              >
                <TableSortLabel
                  active={sortConfig.key === col.key}
                  direction={sortConfig.key === col.key ? sortConfig.direction : 'asc'}
                  onClick={() => onSort(col.key)}
                >
                  {col.title}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, index) => (
            <TableRow hover key={`${row.date}_${index}`}>
              {xlsColumns.map((col) => (
                <TableCell key={col.key}>
                  {getCellValue(row, col.key, timeZone)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

ManagementReportTable.propTypes = {
  data: PropTypes.array,
  timeZone: PropTypes.string,
  sortConfig: PropTypes.object,
  onSort: PropTypes.func,
};

function UsersAlertsManagementReport({ selectedProject, isVisible, close, isFetching }, context) {
  const timeZone = useSelector(state => state.local.timeZone);
  const options = useSelector(state => state.leaksList.optionList.options);
  const [isLoading, setIsLoading] = useState(false);
  const [mode, setMode] = useState('monthly');
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().substring(0, 7));
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  // Captured when a report is generated so exports are named after the data
  // they contain, not whatever the pickers show at download time.
  const [fileLabel, setFileLabel] = useState(null);
  const [includeAqs, setIncludeAqs] = useState(false);
  const [usersInReport, setUsersInReport] = useState([]);
  const [usersFilter, setUsersFilter] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const dispatch = useDispatch();
  const [data, setData] = useState([]);

  const projectId = selectedProject ? selectedProject.ID : null;
  const prevProjectIdRef = useRef(projectId);
  useEffect(() => {
    if (prevProjectIdRef.current === projectId) {
      return;
    }
    // The report belongs to the previous project's customer - drop it and
    // close the modal so it can't be read against the new project.
    prevProjectIdRef.current = projectId;
    setData([]);
    setUsersInReport([]);
    setUsersFilter([]);
    setFileLabel(null);
    setSortConfig({ key: null, direction: 'asc' });
    if (isVisible) {
      close();
    }
  }, [projectId, isVisible, close]);

  const onSort = useCallback((key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  const visibleData = useMemo(() => {
    const filtered = usersFilter.length === 0
      ? data
      : data.filter((row) => usersFilter.includes(row.user));
    return sortRows(filtered, sortConfig);
  }, [data, usersFilter, sortConfig]);

  const handleExportExcel = async () => {
    if (!visibleData.length) {
      alert('No data to export!');
      return;
    }
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Alerts Management');

    worksheet.columns = xlsColumns.map((col, index) => ({
      header: col.title,
      key: `col${index}`,
      width: col.width.wch,
    }));

    worksheet.autoFilter = `A1:J${visibleData.length + 1}`;

    visibleData.forEach((item) => {
      worksheet.addRow(xlsColumns.map((col) => `${getCellValue(item, col.key, timeZone)}`));
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/octet-stream' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `${selectedProject.Name} - ${fileLabel || reportFileLabel(mode, selectedMonth, customFrom, customTo)} users.xlsx`;
    link.click();
  };

  async function exportPdf() {
    const doc = new jsPDF({ orientation: 'portrait' });

    // The jsPDF built-in fonts can't render Hebrew; fall back to them only if
    // the Hebrew font fails to load so the export still works.
    let pdfFont = 'helvetica';
    try {
      await registerHebrewPdfFont(doc);
      pdfFont = 'Alef';
    } catch (err) {
      console.error('Failed to load Hebrew PDF font', err);
    }
    applyPdfRtlSupport(doc);

    const tableBody = visibleData.map((row) =>
      xlsColumns.map((col) => `${getCellValue(row, col.key, timeZone)}`)
    );
    const tableHeader = xlsColumns.map(col => col.title);
    const horizontalPadding = 10;
    doc.setFont(pdfFont);
    doc.setFontSize(16);
    doc.text(`Users Alerts Management Report`, horizontalPadding, 10);
    doc.setFontSize(12);
    doc.text(`generate for ${selectedProject.Name}`, horizontalPadding, 25);

    doc.autoTable({
      styles: { valign: 'center', fontSize: 5, font: pdfFont },
      startX: 0,
      startY: 35,
      columns: tableHeader,
      body: tableBody,
      minCellWidth: 5,
      cellPadding: 2,
    });
    doc.save(`${selectedProject.Name} - ${fileLabel || reportFileLabel(mode, selectedMonth, customFrom, customTo)} users.pdf`);
  }

  const onGeneratedReport = ({ mode: reportMode, month: reportMonth, from, to }) => {
    let request;
    if (reportMode === 'custom') {
      request = dispatch(fetchUsersAlertsManagmentReportRange(from, to, includeAqs));
    } else {
      const [year, month] = reportMonth.split('-');
      request = dispatch(fetchUsersAlertsManagmentReport(month, year, includeAqs));
    }
    setIsLoading(true);
    request.then((response) => {
      if (response.status) {
        const users = new Set();
        const reportData = response.data.map((item, index) => {
          let type = undefined;
          let status = undefined;

          if (item.type) {
             type = typeof item.type === 'string' ? item.type : options.AlertType.find((opt) => opt.value === item.type)?.label;
          }

          if (item.status !== undefined) {
            status = typeof item.status === 'string' ? item.status : options.AlertState.find((opt) => opt.value === item.status)?.label || item.status;
          }

          users.add(item.user)
          return { ...item, type, status}
        })

        setData(reportData);
        setUsersInReport(Array.from(users));
        setFileLabel(reportFileLabel(reportMode, reportMonth, from, to));
      }
    }).finally(() => {
      setIsLoading(false);
    });
  };

  return (
    <AppModal
      closeIcon
      fullWidth
      maxWidth='xl'
      open={isVisible}
      title={context.t('users_alerts_management_report')}
      onClose={close}
      content={(
        <div style={{ height: '75vh', overflow: 'hidden' }}>
          {!isFetching && selectedProject && (
            <div style={{ display: 'flex' }}>
              <div>
                <Typography component='p' variant='p'>{context.t('create_report')}</Typography>
                <ReportFilter
                  selectedProject={selectedProject}
                  dispatch={dispatch}
                  isVisible={isVisible}
                  context={context}
                  isGenereated={isLoading}
                  onGeneratedReport={onGeneratedReport}
                  mode={mode}
                  setMode={setMode}
                  selectedMonth={selectedMonth}
                  setSelectedMonth={setSelectedMonth}
                  customFrom={customFrom}
                  setCustomFrom={setCustomFrom}
                  customTo={customTo}
                  setCustomTo={setCustomTo}
                  includeAqs={includeAqs}
                  setIncludeAqs={setIncludeAqs}
                  usersList={usersInReport}
                  setUsersFilter={(users) => {
                    setUsersFilter(users.map((u) => u.value))
                  }}
                />
                <div className='iq100b-export-buttons'>
                  <Button
                    disabled={isLoading}
                    style={exportButtonStyle}
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    onClick={exportPdf}
                  >
                    {context.t('save_as_pdf')}
                  </Button>
                  <Button
                    disabled={isLoading || !data.length}
                    style={exportButtonStyle}
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    onClick={handleExportExcel}
                  >
                    {context.t('save_as_excel')}
                  </Button>
                </div>
              </div>
              <div style={{
                display: 'inline-flex',
                flex: 1,
                margin: 10,
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden'
              }}>
                {!isLoading && data.length ? (
                  <ManagementReportTable timeZone={timeZone} data={visibleData} sortConfig={sortConfig} onSort={onSort} />
                ) : isLoading && !data.length ? (
                  <ThreeCircles height="30" width="30" color={'#6090B1'} />
                ) : null}
              </div>
            </div>
          )}
        </div>
      )}
    />
  );
}

UsersAlertsManagementReport.contextTypes = {
  t: PropTypes.func.isRequired,
};

UsersAlertsManagementReport.propTypes = {
  close: PropTypes.func,
  isFetching: PropTypes.bool,
  isVisible: PropTypes.bool,
  selectedProject: PropTypes.object,
  users: PropTypes.array,
};

export default React.memo(UsersAlertsManagementReport);
